// Tiny safe expression engine for the cabinet DSL.
//
// Grammar:
//   expr      := ternary
//   ternary   := orExpr ("?" expr ":" expr)?
//   orExpr    := andExpr ("||" andExpr)*
//   andExpr   := cmpExpr ("&&" cmpExpr)*
//   cmpExpr   := addExpr (("=="|"!="|"<"|"<="|">"|">=") addExpr)?
//   addExpr   := mulExpr (("+"|"-") mulExpr)*
//   mulExpr   := unary  (("*"|"/"|"%") unary)*
//   unary     := ("-"|"+"|"!") unary | primary
//   primary   := NUMBER | IDENT ["(" args ")"] | "(" expr ")"
//   args      := expr ("," expr)*
//
// No string literals, no property access, no statements, no eval. The
// only callable identifiers are the function whitelist in `FUNCTIONS`.
// Identifiers resolve through the caller-provided `Scope`.
//
// Booleans are encoded as 0/1; `enabled`-style expressions treat any
// non-zero value as true.

// ── AST ──────────────────────────────────────────────────────────

export type Ast =
  | { kind: "num"; value: number }
  | { kind: "id"; name: string; at: number }
  | { kind: "unary"; op: "-" | "+" | "!"; arg: Ast; at: number }
  | {
      kind: "bin";
      op: BinOp;
      l: Ast;
      r: Ast;
      at: number;
    }
  | { kind: "tern"; cond: Ast; then: Ast; els: Ast; at: number }
  | { kind: "call"; name: string; args: Ast[]; at: number };

type BinOp =
  | "+" | "-" | "*" | "/" | "%"
  | "==" | "!=" | "<" | "<=" | ">" | ">="
  | "&&" | "||";

// ── Lexer ────────────────────────────────────────────────────────

type TokKind =
  | "num" | "id"
  | "(" | ")" | "," | "?" | ":"
  | "+" | "-" | "*" | "/" | "%"
  | "==" | "!=" | "<" | "<=" | ">" | ">="
  | "&&" | "||" | "!";

interface Tok {
  kind: TokKind;
  text: string;
  /** Character offset in the source — used for error messages. */
  at: number;
}

const ID_HEAD = /[A-Za-z_]/;
const ID_TAIL = /[A-Za-z_0-9]/;
const NUM_HEAD = /[0-9.]/;

function lex(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === " " || c === "\t" || c === "\n" || c === "\r") {
      i++;
      continue;
    }
    const at = i;
    // 2-char operators first
    const two = src.slice(i, i + 2);
    if (two === "==" || two === "!=" || two === "<=" || two === ">=" || two === "&&" || two === "||") {
      out.push({ kind: two as TokKind, text: two, at });
      i += 2;
      continue;
    }
    if ("()+-*/%,<>?:!".includes(c)) {
      out.push({ kind: c as TokKind, text: c, at });
      i++;
      continue;
    }
    if (NUM_HEAD.test(c)) {
      let j = i;
      while (j < src.length && NUM_HEAD.test(src[j]!)) j++;
      // Reject double-dot floats early.
      const text = src.slice(i, j);
      if ((text.match(/\./g) ?? []).length > 1) {
        throw new ExprError(`malformed number "${text}"`, at);
      }
      out.push({ kind: "num", text, at });
      i = j;
      continue;
    }
    if (ID_HEAD.test(c)) {
      let j = i + 1;
      while (j < src.length && ID_TAIL.test(src[j]!)) j++;
      out.push({ kind: "id", text: src.slice(i, j), at });
      i = j;
      continue;
    }
    throw new ExprError(`unexpected character "${c}"`, at);
  }
  return out;
}

// ── Parser ───────────────────────────────────────────────────────

export class ExprError extends Error {
  constructor(message: string, public at: number) {
    super(`expr@${at}: ${message}`);
  }
}

interface ParserState {
  src: string;
  toks: Tok[];
  i: number;
}

function peek(p: ParserState): Tok | null {
  return p.toks[p.i] ?? null;
}
function eat(p: ParserState): Tok {
  const t = p.toks[p.i];
  if (!t) throw new ExprError("unexpected end of expression", p.src.length);
  p.i++;
  return t;
}
function expect(p: ParserState, kind: TokKind): Tok {
  const t = peek(p);
  if (!t || t.kind !== kind) {
    const at = t?.at ?? p.src.length;
    throw new ExprError(`expected "${kind}"`, at);
  }
  return eat(p);
}

export function parse(src: string): Ast {
  const toks = lex(src);
  const p: ParserState = { src, toks, i: 0 };
  const ast = parseExpr(p);
  if (peek(p)) {
    throw new ExprError(`unexpected token "${peek(p)!.text}"`, peek(p)!.at);
  }
  return ast;
}

function parseExpr(p: ParserState): Ast {
  return parseTernary(p);
}

function parseTernary(p: ParserState): Ast {
  const cond = parseOr(p);
  if (peek(p)?.kind === "?") {
    const at = eat(p).at;
    const then = parseExpr(p);
    expect(p, ":");
    const els = parseExpr(p);
    return { kind: "tern", cond, then, els, at };
  }
  return cond;
}

function parseOr(p: ParserState): Ast {
  let l = parseAnd(p);
  while (peek(p)?.kind === "||") {
    const at = eat(p).at;
    const r = parseAnd(p);
    l = { kind: "bin", op: "||", l, r, at };
  }
  return l;
}

function parseAnd(p: ParserState): Ast {
  let l = parseCmp(p);
  while (peek(p)?.kind === "&&") {
    const at = eat(p).at;
    const r = parseCmp(p);
    l = { kind: "bin", op: "&&", l, r, at };
  }
  return l;
}

function parseCmp(p: ParserState): Ast {
  const l = parseAdd(p);
  const t = peek(p);
  if (!t) return l;
  if (t.kind === "==" || t.kind === "!=" || t.kind === "<" || t.kind === "<=" || t.kind === ">" || t.kind === ">=") {
    const at = eat(p).at;
    const r = parseAdd(p);
    return { kind: "bin", op: t.kind, l, r, at };
  }
  return l;
}

function parseAdd(p: ParserState): Ast {
  let l = parseMul(p);
  while (peek(p)?.kind === "+" || peek(p)?.kind === "-") {
    const t = eat(p);
    const r = parseMul(p);
    l = { kind: "bin", op: t.kind as "+" | "-", l, r, at: t.at };
  }
  return l;
}

function parseMul(p: ParserState): Ast {
  let l = parseUnary(p);
  while (peek(p)?.kind === "*" || peek(p)?.kind === "/" || peek(p)?.kind === "%") {
    const t = eat(p);
    const r = parseUnary(p);
    l = { kind: "bin", op: t.kind as "*" | "/" | "%", l, r, at: t.at };
  }
  return l;
}

function parseUnary(p: ParserState): Ast {
  const t = peek(p);
  if (t && (t.kind === "-" || t.kind === "+" || t.kind === "!")) {
    const at = eat(p).at;
    const arg = parseUnary(p);
    return { kind: "unary", op: t.kind, arg, at };
  }
  return parsePrimary(p);
}

function parsePrimary(p: ParserState): Ast {
  const t = peek(p);
  if (!t) throw new ExprError("unexpected end of expression", p.src.length);
  if (t.kind === "num") {
    eat(p);
    const value = Number(t.text);
    if (!Number.isFinite(value)) {
      throw new ExprError(`bad number "${t.text}"`, t.at);
    }
    return { kind: "num", value };
  }
  if (t.kind === "id") {
    eat(p);
    if (peek(p)?.kind === "(") {
      eat(p);
      const args: Ast[] = [];
      if (peek(p)?.kind !== ")") {
        args.push(parseExpr(p));
        while (peek(p)?.kind === ",") {
          eat(p);
          args.push(parseExpr(p));
        }
      }
      expect(p, ")");
      return { kind: "call", name: t.text, args, at: t.at };
    }
    return { kind: "id", name: t.text, at: t.at };
  }
  if (t.kind === "(") {
    eat(p);
    const inner = parseExpr(p);
    expect(p, ")");
    return inner;
  }
  throw new ExprError(`unexpected token "${t.text}"`, t.at);
}

// ── Evaluator ────────────────────────────────────────────────────

export type Scope = Record<string, number>;

/** Whitelisted functions. All take and return numbers. Booleans round-
 *  trip through 0/1. */
const FUNCTIONS: Record<string, (args: number[], at: number) => number> = {
  min: (xs) => Math.min(...xs),
  max: (xs) => Math.max(...xs),
  abs: ([x], at) => check(Math.abs(x!), at),
  floor: ([x], at) => check(Math.floor(x!), at),
  ceil: ([x], at) => check(Math.ceil(x!), at),
  round: ([x], at) => check(Math.round(x!), at),
  clamp: ([x, lo, hi], at) => {
    if (x === undefined || lo === undefined || hi === undefined) {
      throw new ExprError("clamp expects 3 args", at);
    }
    return check(Math.max(lo, Math.min(hi, x)), at);
  },
  mod: ([a, b], at) => {
    if (a === undefined || b === undefined) throw new ExprError("mod expects 2 args", at);
    if (b === 0) throw new ExprError("mod by zero", at);
    return check(((a % b) + b) % b, at);
  },
  if: ([c, a, b], at) => {
    if (c === undefined || a === undefined || b === undefined) {
      throw new ExprError("if expects 3 args", at);
    }
    return c !== 0 ? a : b;
  },
};

function check(n: number, at: number): number {
  if (!Number.isFinite(n)) {
    throw new ExprError(`non-finite result (${n})`, at);
  }
  return n;
}

export function evalAst(ast: Ast, scope: Scope): number {
  switch (ast.kind) {
    case "num":
      return ast.value;
    case "id": {
      const v = scope[ast.name];
      if (v === undefined) {
        throw new ExprError(`unknown identifier "${ast.name}"`, ast.at);
      }
      if (typeof v !== "number" || !Number.isFinite(v)) {
        throw new ExprError(`identifier "${ast.name}" is not a finite number`, ast.at);
      }
      return v;
    }
    case "unary": {
      const v = evalAst(ast.arg, scope);
      if (ast.op === "-") return check(-v, ast.at);
      if (ast.op === "+") return check(+v, ast.at);
      return v === 0 ? 1 : 0; // "!"
    }
    case "bin": {
      // Short-circuit for && / || so partial NaN doesn't poison the result.
      if (ast.op === "&&") {
        const l = evalAst(ast.l, scope);
        if (l === 0) return 0;
        return evalAst(ast.r, scope) === 0 ? 0 : 1;
      }
      if (ast.op === "||") {
        const l = evalAst(ast.l, scope);
        if (l !== 0) return 1;
        return evalAst(ast.r, scope) === 0 ? 0 : 1;
      }
      const l = evalAst(ast.l, scope);
      const r = evalAst(ast.r, scope);
      switch (ast.op) {
        case "+": return check(l + r, ast.at);
        case "-": return check(l - r, ast.at);
        case "*": return check(l * r, ast.at);
        case "/":
          if (r === 0) throw new ExprError("division by zero", ast.at);
          return check(l / r, ast.at);
        case "%":
          if (r === 0) throw new ExprError("modulo by zero", ast.at);
          return check(l % r, ast.at);
        case "==": return l === r ? 1 : 0;
        case "!=": return l !== r ? 1 : 0;
        case "<":  return l <  r ? 1 : 0;
        case "<=": return l <= r ? 1 : 0;
        case ">":  return l >  r ? 1 : 0;
        case ">=": return l >= r ? 1 : 0;
      }
      // unreachable
      throw new ExprError(`unsupported operator ${ast.op as string}`, ast.at);
    }
    case "tern": {
      const c = evalAst(ast.cond, scope);
      return c !== 0 ? evalAst(ast.then, scope) : evalAst(ast.els, scope);
    }
    case "call": {
      const fn = FUNCTIONS[ast.name];
      if (!fn) throw new ExprError(`unknown function "${ast.name}"`, ast.at);
      const args = ast.args.map((a) => evalAst(a, scope));
      return fn(args, ast.at);
    }
  }
}

// ── Public conveniences ─────────────────────────────────────────

/** Parse + eval in one shot. Throws `ExprError` with a `.at` offset on
 *  failure. Use `parse()` directly if you want to cache the AST. */
export function evaluate(src: string, scope: Scope): number {
  return evalAst(parse(src), scope);
}

/** Coerce a value into a parsed AST. Numbers wrap into `{kind:"num"}`;
 *  strings are parsed. Useful when class JSON allows either form for
 *  params and box corners. */
export function asAst(v: string | number): Ast {
  if (typeof v === "number") {
    if (!Number.isFinite(v)) {
      throw new ExprError(`non-finite literal (${v})`, 0);
    }
    return { kind: "num", value: v };
  }
  return parse(v);
}

/** Evaluate either a string-expression or a literal number against the
 *  scope. Used everywhere PartDef fields accept both forms. */
export function evalValue(v: string | number, scope: Scope): number {
  if (typeof v === "number") {
    if (!Number.isFinite(v)) {
      throw new ExprError(`non-finite literal (${v})`, 0);
    }
    return v;
  }
  return evaluate(v, scope);
}

/** Evaluate a layered set of param definitions in declaration order so
 *  later params can reference earlier ones. Detects self-/forward-
 *  references with a structured error. Returns a flat number scope. */
export function evalParams(
  defs: Record<string, string | number>,
  base: Scope,
): Scope {
  const out: Scope = { ...base };
  for (const [name, src] of Object.entries(defs)) {
    try {
      out[name] = evalValue(src, out);
    } catch (e) {
      if (e instanceof ExprError) {
        throw new ExprError(`in param "${name}": ${e.message}`, e.at);
      }
      throw e;
    }
  }
  return out;
}
